import { forwardRef, useMemo } from 'react';
import { cx } from 'class-variance-authority';
import { BaseTypeMap, Component, ComponentProps } from './types/Component';
import { Button, ButtonProps } from './Button';
import { Box } from './Box';
import { pipe } from 'fp-ts/lib/function';
import { fromPredicate, map, getOrElseW } from 'fp-ts/lib/Option';

type LoadingButtonTypeMap = BaseTypeMap<
  'button',
  { loadingLabel: string; isLoading?: boolean } & ButtonProps
>;

export type LoadingButtonProps = ComponentProps<LoadingButtonTypeMap>;

export const LoadingButton: Component<LoadingButtonTypeMap> = forwardRef(
  function LoadingButton(
    {
      children,
      className,
      isLoading,
      loadingLabel,
      endIcon,
      startIcon,
      disabled,
      inverted,
      ...rest
    },
    ref
  ) {
    const loadingIcon = useMemo(
      () => (
        <svg
          className="animate-spin"
          fill="currentColor"
          width="1em"
          height="1em"
          viewBox="0 0 24 24"
        >
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          ></path>
        </svg>
      ),
      []
    );

    const _startIcon = useMemo(
      () =>
        pipe(
          children,
          fromPredicate((x) => !x),
          map(() => (isLoading ? loadingIcon : startIcon)),
          getOrElseW(() => undefined)
        ),
      [children, isLoading, startIcon, loadingIcon]
    );

    const content = useMemo(
      () =>
        pipe(
          children,
          fromPredicate((x) => !!x),
          map(() => (
            <>
              {isLoading ? (
                <Box
                  as="span"
                  className="absolute bottom-0 left-0 right-0 top-0 flex items-center justify-center text-lg"
                >
                  {loadingIcon}
                </Box>
              ) : null}
              <Box
                as="span"
                aria-hidden={isLoading ? 'true' : undefined}
                className={isLoading ? 'invisible' : undefined}
              >
                {children}
              </Box>
            </>
          )),
          getOrElseW(() => null)
        ),
      [children, isLoading, loadingIcon]
    );

    return (
      <Button
        ref={ref}
        className={cx('relative', className)}
        {...rest}
        aria-label={isLoading ? loadingLabel : undefined}
        endIcon={isLoading ? null : endIcon}
        startIcon={_startIcon}
        disabled={isLoading || disabled}
        isLoading={isLoading}
        inverted={inverted}
      >
        {content}
      </Button>
    );
  }
);
